import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  BookOpen,
  CalendarCheck,
  Check,
  CheckCircle2,
  ClipboardCheck,
  Database,
  FileText,
  GraduationCap,
  History,
  Lock,
  MessageSquare,
  Paperclip,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  UserCheck,
} from "lucide-react";
import AIAssistField from "../components/AIAssistField";
import RatingInput, { RatingDisplay } from "../components/RatingInput";
import { ErrorState, LoadingState } from "../components/PageState";
import {
  appraisalService,
  type AnswerInput,
  type AppraisalQuestion,
  type AppraisalStatus,
  type AnswerRole,
} from "../services/appraisalService";
import { AI_UNAVAILABLE, aiService } from "../services/aiService";
import {
  actionLabel,
  ACTIONS_NEEDING_INPUT,
  answeredCount,
  progressPercent,
  stageLabel,
  statusTone,
  unansweredRequired,
  writableRole,
} from "../hooks/useAppraisalStages";
import { useAuthStore } from "../stores/authStore";

type Draft = Record<number, { value: string; ai_generated: boolean; ai_edited: boolean }>;

const TONE_CLASSES: Record<string, string> = {
  neutral: "bg-white/15 text-white ring-1 ring-white/25",
  progress: "bg-white/15 text-white ring-1 ring-white/25",
  waiting: "bg-gold-400 text-navy-900",
  warning: "bg-maroon-500 text-white",
  done: "bg-emerald-400 text-emerald-950",
};

/** An icon per evidence source, so the panels are scannable before they are read. */
const EVIDENCE_ICONS: Record<string, typeof CalendarCheck> = {
  attendance: CalendarCheck,
  training: GraduationCap,
  syllabus: BookOpen,
  exams: ClipboardCheck,
  results: ClipboardCheck,
  discipline: ShieldCheck,
  performance: TrendingUp,
};

/** Read a saved answer back into the single string the form edits. */
function answerToDraftValue(
  question: AppraisalQuestion,
  answer: { value_text: string; value_number: string | null; value_date: string | null; value_choice: string[] } | undefined,
): string {
  if (!answer) return "";
  if (question.question_type === "date") return answer.value_date ?? "";
  if (["rating", "number", "percentage"].includes(question.question_type)) {
    return answer.value_number ?? "";
  }
  if (["dropdown", "single_choice", "multi_choice"].includes(question.question_type)) {
    return (answer.value_choice ?? []).join(", ");
  }
  return answer.value_text ?? "";
}

/** Turn the form's drafts back into the API's shape. */
function draftToAnswerInput(question: AppraisalQuestion, draft: Draft[number]): AnswerInput {
  const base: AnswerInput = {
    question: question.id,
    ai_generated: draft.ai_generated,
    ai_edited: draft.ai_edited,
  };
  if (question.question_type === "date") return { ...base, value_date: draft.value || null };
  if (["rating", "number", "percentage"].includes(question.question_type)) {
    return { ...base, value_number: draft.value === "" ? null : draft.value };
  }
  if (["dropdown", "single_choice", "multi_choice"].includes(question.question_type)) {
    return {
      ...base,
      value_choice: draft.value ? draft.value.split(",").map((v) => v.trim()) : [],
    };
  }
  return { ...base, value_text: draft.value };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

/** A card with a quiet header, used for every block on the page. */
function Panel({
  title,
  icon: Icon,
  description,
  aside,
  children,
}: {
  title: string;
  icon?: typeof Target;
  description?: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="card overflow-hidden">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-secondary-100 bg-secondary-50/60 px-5 py-3.5">
        <div>
          <h2 className="inline-flex items-center gap-2 text-sm font-semibold text-secondary-900">
            {Icon && <Icon className="h-4 w-4 text-secondary-400" aria-hidden />}
            {title}
          </h2>
          {description && (
            <p className="mt-0.5 max-w-2xl text-xs leading-relaxed text-secondary-500">
              {description}
            </p>
          )}
        </div>
        {aside}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

/**
 * Where the appraisal is along its own path.
 *
 * Built from `appraisal.stages` rather than a fixed list, because a
 * support-staff form genuinely has fewer steps than a teacher's and drawing
 * the missing ones as "still to come" would be a lie about the process.
 */
function StageTrack({
  stages,
  status,
  returnedTo,
}: {
  stages: AppraisalStatus[];
  status: AppraisalStatus;
  returnedTo: string;
}) {
  // A returned appraisal has no place of its own on the track; it sits at the
  // stage it will resume from, marked as sent back rather than as progress.
  const anchor = status === "returned_for_revision" ? (returnedTo as AppraisalStatus) : status;
  const currentIndex = stages.indexOf(anchor);

  return (
    <ol className="scrollbar-hover flex items-start gap-1 overflow-x-auto pb-1">
      {stages.map((stage, index) => {
        const done = currentIndex >= 0 && index < currentIndex;
        const current = index === currentIndex;
        const returned = current && status === "returned_for_revision";
        return (
          <li key={stage} className="flex min-w-[5.5rem] flex-1 flex-col items-center gap-1.5">
            <div className="flex w-full items-center">
              <span
                className={`h-0.5 flex-1 rounded-full ${
                  index === 0 ? "bg-transparent" : done || current ? "bg-navy-500" : "bg-navy-100"
                }`}
              />
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                  returned
                    ? "bg-maroon-600 text-white"
                    : current
                      ? "bg-white text-navy-800 ring-2 ring-navy-600"
                      : done
                        ? "bg-navy-600 text-white"
                        : "bg-navy-100 text-navy-400"
                }`}
              >
                {returned ? (
                  <RotateCcw className="h-3 w-3" aria-hidden />
                ) : done ? (
                  <Check className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  index + 1
                )}
              </span>
              <span
                className={`h-0.5 flex-1 rounded-full ${
                  index === stages.length - 1
                    ? "bg-transparent"
                    : done
                      ? "bg-navy-500"
                      : "bg-navy-100"
                }`}
              />
            </div>
            <span
              className={`text-center text-[11px] leading-tight ${
                current ? "font-semibold text-secondary-900" : "text-secondary-500"
              }`}
            >
              {stageLabel(stage)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function EvidencePanel({
  slug,
  data,
  asOf,
  frozen,
}: {
  slug: string;
  data: Record<string, unknown>;
  asOf: string;
  frozen: boolean;
}) {
  const hasData = data.has_data !== false;
  const note = typeof data.note === "string" ? data.note : null;
  const rows = Object.entries(data).filter(
    ([key, value]) =>
      !["has_data", "note"].includes(key) &&
      (typeof value === "number" || typeof value === "string"),
  );
  const Icon = EVIDENCE_ICONS[slug] ?? Database;

  return (
    <div className="rounded-xl border border-secondary-200 bg-white p-4 transition-colors hover:border-navy-200">
      <div className="mb-3 flex items-start justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-sm font-semibold capitalize text-secondary-900">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-navy-50">
            <Icon className="h-4 w-4 text-navy-600" aria-hidden />
          </span>
          {slug.replace(/_/g, " ")}
        </h3>
        <span
          className="inline-flex shrink-0 items-center gap-1 text-[11px] text-secondary-400"
          title={
            frozen
              ? "Preserved when the appraisal closed, so it matches what the judgements were made against."
              : "Read from the school records just now."
          }
        >
          {frozen && <Lock className="h-3 w-3" aria-hidden />}
          {frozen ? "preserved" : "as of"} {new Date(asOf).toLocaleDateString()}
        </span>
      </div>

      {!hasData ? (
        <p className="rounded-lg border border-dashed border-secondary-200 px-3 py-4 text-center text-xs text-secondary-400">
          {note ?? "No records for this period."}
        </p>
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {rows.map(([key, value]) => (
              <div key={key} className="rounded-lg bg-secondary-50 px-3 py-2">
                <dt className="text-[11px] capitalize leading-tight text-secondary-500">
                  {key.replace(/_/g, " ")}
                </dt>
                <dd className="mt-0.5 text-lg font-semibold leading-tight text-secondary-900">
                  {String(value)}
                </dd>
              </div>
            ))}
          </dl>
          {note && (
            <p className="mt-3 text-xs leading-relaxed text-secondary-500">{note}</p>
          )}
        </>
      )}
    </div>
  );
}

export default function AppraisalDetail() {
  const { id } = useParams<{ id: string }>();
  const appraisalId = Number(id);
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [draft, setDraft] = useState<Draft>({});
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [transitionInput, setTransitionInput] = useState("");
  const [meetingDate, setMeetingDate] = useState("");

  const detail = useQuery({
    queryKey: ["appraisal", appraisalId],
    queryFn: () => appraisalService.getAppraisal(appraisalId),
    enabled: Number.isFinite(appraisalId),
  });

  const evidence = useQuery({
    queryKey: ["appraisal-evidence", appraisalId],
    queryFn: () => appraisalService.getEvidence(appraisalId),
    enabled: Number.isFinite(appraisalId),
  });

  const aiStatus = useQuery({
    queryKey: ["ai-status"],
    queryFn: aiService.getStatus,
  });

  const suggestions = useQuery({
    queryKey: ["appraisal-suggestions", appraisalId],
    queryFn: () => appraisalService.getSuggestedScores(appraisalId),
    enabled: Number.isFinite(appraisalId),
  });

  const appraisal = detail.data;

  const role: AnswerRole | null = useMemo(() => {
    if (!appraisal || !user) return null;
    // The server enforces this too; deciding it here only governs which inputs
    // render, so the worst a mismatch can do is hide a field, never grant one.
    return writableRole(appraisal, user.id, appraisal.staff_user);
  }, [appraisal, user]);

  // Seed the form from whatever has been answered already.
  useEffect(() => {
    if (!appraisal) return;
    const next: Draft = {};
    for (const section of appraisal.form) {
      for (const question of section.questions) {
        const saved = appraisal.answers.find(
          (a) => a.question === question.id && (!role || a.role === role),
        );
        next[question.id] = {
          value: answerToDraftValue(question, saved),
          ai_generated: saved?.ai_generated ?? false,
          ai_edited: saved?.ai_edited ?? false,
        };
      }
    }
    setDraft(next);
  }, [appraisal, role]);

  const save = useMutation({
    mutationFn: async () => {
      if (!appraisal || !role) return [];
      const visible = new Set(appraisal.visible_questions[role] ?? []);
      const payload = appraisal.form
        .flatMap((s) => s.questions)
        .filter(
          (q) =>
            visible.has(q.id) &&
            (q.answered_by === role || q.answered_by === "both") &&
            (draft[q.id]?.value ?? "") !== "",
        )
        .map((q) => draftToAnswerInput(q, draft[q.id]));
      return appraisalService.saveAnswers(appraisalId, payload);
    },
    onSuccess: () => {
      setMessage("Saved.");
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["appraisal", appraisalId] });
    },
    onError: () => setError("Those answers could not be saved."),
  });

  const transition = useMutation({
    mutationFn: (action: string) =>
      appraisalService.transition(appraisalId, action, {
        comment: transitionInput,
        acknowledgement_comment: action === "acknowledge" ? transitionInput : undefined,
        review_meeting_held_on:
          action === "record_review_meeting" ? meetingDate : undefined,
        review_meeting_notes:
          action === "record_review_meeting" ? transitionInput : undefined,
      }),
    onSuccess: () => {
      setTransitionInput("");
      setMeetingDate("");
      setMessage("Done.");
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["appraisal", appraisalId] });
      void queryClient.invalidateQueries({ queryKey: ["appraisal-dashboard"] });
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: Record<string, unknown> } })?.response
        ?.data;
      setError(
        data
          ? Object.values(data).flat().join(" ")
          : "That step could not be completed.",
      );
    },
  });

  if (detail.isLoading) return <LoadingState label="Loading appraisal…" />;
  if (detail.isError || !appraisal) {
    return (
      <ErrorState
        message="This appraisal could not be loaded. You may not have access to it."
        retry={() => void detail.refetch()}
      />
    );
  }

  const status = aiStatus.data ?? AI_UNAVAILABLE;
  const percent = progressPercent(appraisal.stages, appraisal.status);
  const visibleForRole = new Set(role ? appraisal.visible_questions[role] ?? [] : []);
  const myQuestions = role
    ? appraisal.form
        .flatMap((s) => s.questions)
        .filter(
          (q) =>
            visibleForRole.has(q.id) &&
            (q.answered_by === role || q.answered_by === "both"),
        )
    : [];
  const stillRequired = unansweredRequired(
    myQuestions,
    draft,
    Array.from(visibleForRole),
  );

  return (
    <div className="space-y-5">
      {/* Who, where in the process, and how far along */}
      <section className="card overflow-hidden">
        <div className="bg-gradient-to-br from-navy-800 to-navy-950 px-5 py-5 sm:px-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-4">
              <span
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-lg font-bold text-white ring-1 ring-white/20"
                aria-hidden
              >
                {initials(appraisal.staff_name)}
              </span>
              <div className="min-w-0">
                <h1 className="truncate text-2xl font-bold tracking-tight text-white">
                  {appraisal.staff_name}
                </h1>
                <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-navy-200">
                  <span>{appraisal.cycle_name}</span>
                  <span className="text-navy-400" aria-hidden>
                    ·
                  </span>
                  <span>{appraisal.template_name}</span>
                  {appraisal.department_name && (
                    <>
                      <span className="text-navy-400" aria-hidden>
                        ·
                      </span>
                      <span>{appraisal.department_name}</span>
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <span
                className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${
                  TONE_CLASSES[statusTone(appraisal.status)]
                }`}
              >
                {stageLabel(appraisal.status)}
              </span>
              {appraisal.is_overdue && (
                <span className="inline-flex items-center gap-1 rounded-full bg-maroon-600 px-2.5 py-0.5 text-xs font-semibold text-white">
                  <AlertTriangle className="h-3 w-3" aria-hidden />
                  Overdue
                </span>
              )}
            </div>
          </div>

          <div className="mt-5">
            <div className="flex items-end justify-between gap-3 text-xs text-navy-200">
              <span className="inline-flex items-center gap-1.5">
                <UserCheck className="h-3.5 w-3.5" aria-hidden />
                Supervisor: <strong className="font-semibold text-white">
                  {appraisal.supervisor_name}
                </strong>
              </span>
              <span>
                <strong className="text-base font-semibold text-white">{percent}%</strong>{" "}
                through this appraisal
              </span>
            </div>
            <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-navy-700">
              <div
                className="h-full rounded-full bg-gold-400 transition-all duration-500"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        </div>

        {appraisal.stages.length > 1 && (
          <div className="border-t border-secondary-100 px-4 py-4 sm:px-6">
            <StageTrack
              stages={appraisal.stages}
              status={appraisal.status}
              returnedTo={appraisal.returned_to_status}
            />
          </div>
        )}
      </section>

      {appraisal.status === "returned_for_revision" && (
        <div className="flex gap-3 rounded-xl border border-maroon-200 bg-maroon-50 p-4">
          <RotateCcw className="mt-0.5 h-5 w-5 shrink-0 text-maroon-600" aria-hidden />
          <div className="text-sm text-maroon-900">
            <p className="font-semibold">Returned for revision</p>
            <p className="mt-0.5 whitespace-pre-wrap">{appraisal.return_reason}</p>
          </div>
        </div>
      )}

      {appraisal.overall_band && (
        <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
          <p className="text-sm text-emerald-900">
            Result:{" "}
            <strong className="text-base font-semibold">{appraisal.overall_band}</strong>
            {appraisal.overall_score && (
              <span className="text-emerald-700"> · scored {appraisal.overall_score}</span>
            )}
          </p>
        </div>
      )}

      {appraisal.acknowledged_at && appraisal.acknowledgement_comment && (
        <Panel title={`Acknowledged by ${appraisal.staff_name}`} icon={UserCheck}>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-secondary-700">
            {appraisal.acknowledgement_comment}
          </p>
        </Panel>
      )}

      {message && (
        <div
          className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800"
          role="status"
        >
          <CheckCircle2 className="h-4 w-4" aria-hidden />
          {message}
        </div>
      )}
      {error && (
        <div
          className="flex items-center gap-2 rounded-xl border border-maroon-200 bg-maroon-50 px-4 py-3 text-sm font-medium text-maroon-800"
          role="alert"
        >
          <AlertTriangle className="h-4 w-4" aria-hidden />
          {error}
        </div>
      )}

      {/* Evidence */}
      {evidence.data && Object.keys(evidence.data.sources).length > 0 && (
        <Panel
          title="Evidence from the school records"
          icon={Database}
          description="Context for the conversation. Nothing here becomes a score unless this form was configured with an explicit rule for it."
        >
          <div className="grid gap-3 lg:grid-cols-2">
            {Object.entries(evidence.data.sources).map(([slug, data]) => (
              <EvidencePanel
                key={slug}
                slug={slug}
                data={data}
                asOf={evidence.data.as_of}
                frozen={evidence.data.frozen}
              />
            ))}
          </div>
        </Panel>
      )}

      {/* The form */}
      {appraisal.form.map((section, sectionIndex) => {
        const mine = section.questions.filter(
          (q) =>
            role !== null &&
            visibleForRole.has(q.id) &&
            (q.answered_by === role || q.answered_by === "both"),
        );
        const done = answeredCount(mine, draft);

        return (
          <section key={section.id} className="card overflow-hidden">
            <header className="flex flex-wrap items-start justify-between gap-3 border-b border-secondary-100 bg-secondary-50/60 px-5 py-3.5">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-navy-700 text-xs font-bold text-white">
                  {sectionIndex + 1}
                </span>
                <div>
                  <h2 className="text-base font-semibold text-secondary-900">
                    {section.title}
                  </h2>
                  {section.description && (
                    <p className="mt-0.5 max-w-2xl text-xs leading-relaxed text-secondary-500">
                      {section.description}
                    </p>
                  )}
                </div>
              </div>
              {mine.length > 0 && (
                <span
                  className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                    done === mine.length
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-white text-secondary-600 ring-1 ring-secondary-200"
                  }`}
                >
                  {done} of {mine.length} answered
                </span>
              )}
            </header>

            <div className="divide-y divide-secondary-100">
              {section.questions.map((question) => {
                const editable =
                  role !== null &&
                  visibleForRole.has(question.id) &&
                  (question.answered_by === role || question.answered_by === "both");
                const current = draft[question.id] ?? {
                  value: "",
                  ai_generated: false,
                  ai_edited: false,
                };
                const suggestion = suggestions.data?.suggestions?.[String(question.id)];
                const savedAnswers = appraisal.answers.filter(
                  (a) => a.question === question.id,
                );
                const answered = current.value.trim() !== "";

                return (
                  <div key={question.id} className="px-5 py-4">
                    <div className="mb-1 flex items-start justify-between gap-3">
                      <label className="text-sm font-semibold leading-snug text-secondary-900">
                        {question.text}
                        {question.is_required && (
                          // `relative` matters: sr-only is absolutely
                          // positioned, and without a positioned parent its
                          // containing block is the viewport, so it escapes the
                          // scrolling main column and stretches the page.
                          <span
                            className="relative ml-1 text-maroon-600"
                            title="Required"
                          >
                            *<span className="sr-only">required</span>
                          </span>
                        )}
                      </label>
                      <span className="flex shrink-0 items-center gap-1.5">
                        {question.data_source && (
                          <span className="inline-flex items-center gap-1 rounded-md bg-navy-50 px-2 py-0.5 text-[11px] font-medium capitalize text-navy-700">
                            <Database className="h-3 w-3" aria-hidden />
                            {question.data_source.replace(/_/g, " ")}
                          </span>
                        )}
                        {editable && answered && (
                          <Check
                            className="h-4 w-4 text-emerald-600"
                            aria-label="Answered"
                          />
                        )}
                      </span>
                    </div>

                    {question.help_text && (
                      <p className="mb-2.5 border-l-2 border-secondary-200 pl-3 text-xs leading-relaxed text-secondary-500">
                        {question.help_text}
                      </p>
                    )}

                    {suggestion && editable && (
                      <p className="mb-2.5 inline-flex items-center gap-1.5 rounded-lg bg-primary-50 px-2.5 py-1.5 text-xs text-primary-900">
                        <TrendingUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        The configured rule suggests{" "}
                        <strong className="font-semibold">{suggestion}</strong> from the
                        evidence. You can enter something different.
                      </p>
                    )}

                    {!editable ? (
                      <div className="space-y-1.5">
                        {savedAnswers.length === 0 && (
                          <p className="text-sm italic text-secondary-400">
                            Not answered yet.
                          </p>
                        )}
                        {savedAnswers.map((answer) => {
                          const shown = answerToDraftValue(question, answer);
                          return (
                            <div
                              key={answer.id}
                              className="rounded-lg border border-secondary-100 bg-secondary-50/60 px-3 py-2"
                            >
                              <div className="mb-1 flex flex-wrap items-center gap-2">
                                <span className="rounded-md bg-white px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-secondary-600 ring-1 ring-secondary-200">
                                  {answer.role === "self" ? "Self" : "Supervisor"}
                                </span>
                                {(answer.ai_generated || answer.ai_edited) && (
                                  <span
                                    className="inline-flex items-center gap-1 rounded-md bg-violet-100 px-1.5 py-0.5 text-[11px] font-medium text-violet-800"
                                    title="This text was drafted by the AI assistant and accepted by a person."
                                  >
                                    <Sparkles className="h-3 w-3" aria-hidden />
                                    {answer.ai_generated ? "AI-written" : "AI-assisted"}
                                  </span>
                                )}
                              </div>
                              {question.question_type === "rating" && shown !== "" ? (
                                <RatingDisplay
                                  value={Number(shown)}
                                  min={question.effective_scale.min}
                                  max={question.effective_scale.max}
                                />
                              ) : (
                                <p className="whitespace-pre-wrap text-sm leading-relaxed text-secondary-800">
                                  {shown || "—"}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : question.question_type === "long_text" ? (
                      <AIAssistField
                        value={current.value}
                        status={status}
                        appraisalId={appraisalId}
                        placeholder="Write in your own words…"
                        onChange={(value, provenance) =>
                          setDraft((d) => ({ ...d, [question.id]: { value, ...provenance } }))
                        }
                      />
                    ) : question.question_type === "rating" ? (
                      <RatingInput
                        value={current.value}
                        label={question.text}
                        min={question.effective_scale.min}
                        max={question.effective_scale.max}
                        onChange={(value) =>
                          setDraft((d) => ({
                            ...d,
                            [question.id]: {
                              value,
                              ai_generated: false,
                              ai_edited: false,
                            },
                          }))
                        }
                      />
                    ) : (
                      <input
                        className="input w-full max-w-md"
                        type={
                          question.question_type === "date"
                            ? "date"
                            : ["number", "percentage"].includes(question.question_type)
                              ? "number"
                              : "text"
                        }
                        value={current.value}
                        onChange={(e) =>
                          setDraft((d) => ({
                            ...d,
                            [question.id]: {
                              value: e.target.value,
                              ai_generated: false,
                              ai_edited: false,
                            },
                          }))
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* Development goals */}
      {appraisal.goals.length > 0 && (
        <Panel title="Objectives and development" icon={Target}>
          <ul className="grid gap-2.5 md:grid-cols-2">
            {appraisal.goals.map((goal) => (
              <li
                key={goal.id}
                className="rounded-xl border border-secondary-200 p-3.5 text-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold text-secondary-900">{goal.title}</span>
                  <span className="shrink-0 rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium capitalize text-secondary-600">
                    {goal.status.replace(/_/g, " ")}
                  </span>
                </div>
                {goal.description && (
                  <p className="mt-1.5 leading-relaxed text-secondary-600">
                    {goal.description}
                  </p>
                )}
                {goal.carried_from && (
                  <p className="mt-2 inline-flex items-center gap-1 text-xs text-secondary-500">
                    <RotateCcw className="h-3 w-3" aria-hidden />
                    Carried forward from the previous appraisal.
                  </p>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* Actions */}
      {appraisal.available_actions.length > 0 && (
        <Panel title="What happens next" icon={CheckCircle2}>
          <div className="space-y-3">
            {appraisal.available_actions.some((a) => ACTIONS_NEEDING_INPUT.has(a)) && (
              <div className="space-y-2">
                {appraisal.available_actions.includes("record_review_meeting") && (
                  <div>
                    <label
                      className="mb-1 block text-xs font-medium text-secondary-600"
                      htmlFor="review-meeting-date"
                    >
                      Date the review meeting was held
                    </label>
                    <input
                      id="review-meeting-date"
                      type="date"
                      className="input max-w-xs"
                      value={meetingDate}
                      onChange={(e) => setMeetingDate(e.target.value)}
                    />
                  </div>
                )}
                <textarea
                  className="input w-full resize-y py-2.5 leading-relaxed"
                  rows={3}
                  placeholder="Comment — required when returning an appraisal, optional otherwise."
                  value={transitionInput}
                  onChange={(e) => setTransitionInput(e.target.value)}
                />
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {appraisal.available_actions.map((action) => (
                <button
                  key={action}
                  type="button"
                  className={
                    action === "return_for_revision"
                      ? "btn border border-maroon-200 bg-white text-maroon-700 hover:bg-maroon-50"
                      : "btn bg-navy-700 text-white hover:bg-navy-800"
                  }
                  disabled={transition.isPending}
                  onClick={() => transition.mutate(action)}
                >
                  {actionLabel(action)}
                </button>
              ))}
            </div>
            {appraisal.available_actions.includes("acknowledge") && (
              <p className="rounded-lg bg-secondary-50 px-3 py-2 text-xs leading-relaxed text-secondary-600">
                Acknowledging records that you have seen this appraisal. You may
                disagree — your comment is kept on the record permanently and cannot be
                edited by anyone.
              </p>
            )}
          </div>
        </Panel>
      )}

      {/* History */}
      {appraisal.comments.length > 0 && (
        <Panel title="Comments" icon={MessageSquare}>
          <ol className="space-y-3">
            {appraisal.comments.map((comment) => (
              <li key={comment.id} className="flex gap-3">
                <span
                  className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy-50 text-[11px] font-bold text-navy-700"
                  aria-hidden
                >
                  {initials(comment.author_name)}
                </span>
                <div className="min-w-0 flex-1 rounded-xl bg-secondary-50 px-3.5 py-2.5">
                  <div className="flex flex-wrap justify-between gap-x-3 text-xs">
                    <span className="font-semibold text-secondary-800">
                      {comment.author_name}
                    </span>
                    <span className="text-secondary-400">
                      {new Date(comment.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-secondary-700">
                    {comment.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </Panel>
      )}

      {appraisal.evidence_files.length > 0 && (
        <Panel title="Attached evidence" icon={FileText}>
          <ul className="space-y-1.5">
            {appraisal.evidence_files.map((file) => (
              <li key={file.id}>
                <a
                  className="flex items-center gap-2.5 rounded-lg border border-secondary-200 px-3 py-2 text-sm transition-colors hover:border-navy-200 hover:bg-navy-50"
                  href={`/api/appraisal/files/${file.id}/download/`}
                >
                  <Paperclip className="h-4 w-4 shrink-0 text-secondary-400" aria-hidden />
                  <span className="font-medium text-navy-700">
                    {file.description || "Attachment"}
                  </span>
                  <span className="ml-auto text-xs text-secondary-400">
                    uploaded by {file.uploaded_by_name}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <p className="flex items-center justify-center gap-1.5 pb-2 text-xs text-secondary-400">
        <History className="h-3.5 w-3.5" aria-hidden />
        Every step on this appraisal is recorded in the audit trail.
      </p>

      {/* Saving stays reachable wherever you are in a long form, and says what
          is still outstanding rather than only what it will do. */}
      {role && (
        // Sticky rather than fixed: the page scrolls inside the layout's main
        // column, so a fixed bar would sit over the sidebar as well.
        <div className="sticky bottom-0 z-30 pb-1 pt-2">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-secondary-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
            <p className="text-sm text-secondary-600">
              {stillRequired.length > 0 ? (
                <span className="inline-flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-gold-600" aria-hidden />
                  <strong className="font-semibold text-secondary-900">
                    {stillRequired.length}
                  </strong>{" "}
                  required{" "}
                  {stillRequired.length === 1 ? "question" : "questions"} still blank
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
                  Everything required has an answer
                </span>
              )}
            </p>
            <button
              type="button"
              className="btn bg-navy-700 px-6 text-white hover:bg-navy-800"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? "Saving…" : "Save answers"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
